import { useEffect, useMemo, useState, useCallback } from 'react';
import { biGet, biPost, errText } from '../bi/biApi';
import { blankFlag } from './ExceptionsSection';

/** All Teacher Desk state + actions (load roster/subjects/existing day, edit, save). */
export default function useDeskState(user, classId, date) {
  const [students, setStudents] = useState([]);
  const [subjectDefs, setSubjectDefs] = useState([]);
  const [att, setAtt] = useState({});          // studentId -> { status, note }
  const [subj, setSubj] = useState({});        // subject -> { topic, homework, dueDate, link }
  const [flags, setFlags] = useState({});      // studentId -> { pendingSubjects[], incompleteNotes, behavior, remark }
  const [activeSubject, setActiveSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!classId) return;
    setLoading(true); setErr(''); setResult(null);
    try {
      const d = await biGet(user, `/api/unified-daily-entry/${encodeURIComponent(classId)}/${date}`);
      setStudents(d.students);
      setSubjectDefs(d.subjects);
      setActiveSubject((cur) => (d.subjects.some((s) => s.name === cur) ? cur : (d.subjects[0] || {}).name || ''));
      const a = {}, sb = {}, fl = {};
      d.students.forEach((s) => { a[s._id] = { status: 'present', note: '' }; });
      if (d.existing) {                    // re-opening a submitted day edits it
        d.existing.attendance.forEach((x) => { a[x.studentId] = { status: x.status === 'late' ? 'present' : x.status, note: x.note || '' }; });
        d.existing.subjects.forEach((x) => { sb[x.subject] = { topic: x.topic, homework: x.homework, dueDate: x.dueDate, link: x.link }; });
        d.existing.studentFlags.forEach((x) => { fl[x.studentId] = { pendingSubjects: x.pendingSubjects, incompleteNotes: x.incompleteNotes, behavior: x.behavior, remark: x.remark }; });
      }
      setAtt(a); setSubj(sb); setFlags(fl);
    } catch (e) { setErr(errText(e)); } finally { setLoading(false); }
  }, [user, classId, date]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { present: 0, absent: 0, leave: 0 };
    students.forEach((s) => { c[(att[s._id] || {}).status || 'present']++; });
    return c;
  }, [students, att]);

  const setStatus = (id, status) => setAtt((p) => ({ ...p, [id]: { ...p[id], status } }));
  const setNote = (id, note) => setAtt((p) => ({ ...p, [id]: { ...p[id], note } }));
  const markAllPresent = () => setAtt((p) => Object.fromEntries(Object.entries(p).map(([k, v]) => [k, { ...v, status: 'present' }])));
  const setField = (name, key, val) => setSubj((p) => ({ ...p, [name]: { ...(p[name] || {}), [key]: val } }));
  const patchFlag = (id, patch) => setFlags((p) => ({ ...p, [id]: { ...blankFlag, ...(p[id] || {}), ...patch } }));
  const togglePending = (id, name) => {
    const cur = (flags[id] || blankFlag).pendingSubjects;
    patchFlag(id, { pendingSubjects: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name] });
  };

  const save = async () => {
    setSaving(true); setErr(''); setResult(null);
    try {
      setResult(await biPost(user, '/api/unified-daily-entry', {
        classId, date, teacherName: user?.name,
        attendance: students.map((s) => ({ studentId: s._id, ...att[s._id] })),
        subjects: subjectDefs.map((d) => ({ subject: d.name, displayName: d.displayName, ...(subj[d.name] || {}) })),
        studentFlags: Object.entries(flags).map(([studentId, f]) => ({ studentId, ...f }))
      }));
    } catch (e) { setErr(errText(e)); } finally { setSaving(false); }
  };

  return {
    students, subjectDefs, att, subj, flags, activeSubject, setActiveSubject, loading, saving, err, setErr, result, counts,
    setStatus, setNote, markAllPresent, setField, patchFlag, togglePending, save
  };
}
